'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

// ── Scroll-triggered fade-in hook ──────────────────────────
function useFadeIn() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

function FadeIn({ children, delay = 0, className = '', style = {} }: { children: React.ReactNode; delay?: number; className?: string; style?: React.CSSProperties }) {
  const { ref, visible } = useFadeIn();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── Nav ────────────────────────────────────────────────────
function PricingNav() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        transition: 'background 0.35s ease, box-shadow 0.35s ease',
        background: scrolled ? 'rgba(245,240,232,0.96)' : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        boxShadow: scrolled ? '0 1px 0 rgba(26,26,26,0.08)' : 'none',
      }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link href="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '8px',
            background: 'linear-gradient(135deg, #8B6914 0%, #b07d5a 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M3 4h12M3 8h8M3 12h10" stroke="#F5F0E8" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </div>
          <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, fontSize: '17px', color: '#1a1a1a', letterSpacing: '-0.01em' }}>
            Notes On This
          </span>
        </Link>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Link
            href="/login"
            style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 400, color: '#1a1a1a', textDecoration: 'none', padding: '8px 14px', borderRadius: '6px' }}
          >
            Sign in
          </Link>
          <Link
            href="/signup"
            style={{
              fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: 500,
              color: '#F5F0E8', textDecoration: 'none',
              padding: '9px 20px', borderRadius: '8px',
              background: 'linear-gradient(135deg, #8B6914 0%, #b07d5a 100%)',
            }}
          >
            Start free
          </Link>
        </div>
      </div>
    </nav>
  );
}

// ── Check icon ─────────────────────────────────────────────
function Check({ color = '#8B6914' }: { color?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: '1px' }}>
      <circle cx="8" cy="8" r="7.5" stroke={color} strokeOpacity="0.25"/>
      <path d="M5 8l2 2 4-4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ── FAQ Accordion ──────────────────────────────────────────
const faqs = [
  {
    q: 'What\'s an AI credit?',
    a: 'Each AI-powered action (weekly recap, insight, summary, assistant message) uses 1 credit. Credits reset at the start of each billing month.',
  },
  {
    q: 'Can I change plans?',
    a: 'Yes, upgrade or downgrade any time. No lock-in. Changes take effect immediately and are prorated.',
  },
  {
    q: 'Can I try Pro first?',
    a: 'Yes, every new workspace gets a 14-day Pro trial automatically. No card required.',
  },
  {
    q: 'Is my data secure?',
    a: 'Yes. Every workspace is fully isolated with row-level security. We never share data between companies.',
  },
  {
    q: 'What counts as a team member?',
    a: 'Anyone you invite to your portal, including admins.',
  },
];

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      style={{
        borderBottom: '1px solid rgba(139,105,20,0.12)',
        overflow: 'hidden',
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '22px 0',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          gap: '16px',
        }}
      >
        <span style={{
          fontFamily: 'Outfit, sans-serif',
          fontSize: '17px',
          fontWeight: 400,
          color: '#1a1a1a',
          letterSpacing: '-0.01em',
          lineHeight: 1.4,
        }}>
          {q}
        </span>
        <div style={{
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          border: '1px solid rgba(139,105,20,0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'transform 0.3s cubic-bezier(0.22,1,0.36,1), background 0.2s',
          transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
          background: open ? 'rgba(139,105,20,0.08)' : 'transparent',
        }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M6 2v8M2 6h8" stroke="#8B6914" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </div>
      </button>
      <div style={{
        maxHeight: open ? '200px' : '0',
        overflow: 'hidden',
        transition: 'max-height 0.4s cubic-bezier(0.22,1,0.36,1)',
      }}>
        <p style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '15px',
          fontWeight: 300,
          color: '#5a4a3a',
          lineHeight: 1.7,
          paddingBottom: '22px',
          margin: 0,
        }}>
          {a}
        </p>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────
export default function PricingPage() {
  const [annual, setAnnual] = useState(false);

  const plans = [
    {
      name: 'Free',
      price: '$0',
      priceSub: '/month',
      annualNote: null,
      badge: null,
      featured: false,
      features: [
        '1 user (solo only)',
        '3 active projects',
        'Core modules: Rhythm + Action Board',
        '20 AI credits/month',
        'Community support',
      ],
      cta: 'Get started free',
      ctaHref: '/signup',
      ctaStyle: 'outline',
    },
    {
      name: 'Pro',
      price: annual ? '$39' : '$49',
      priceSub: '/month',
      annualNote: annual ? 'billed annually' : null,
      saveBadge: annual ? 'Save 20%' : null,
      badge: 'MOST POPULAR',
      featured: true,
      features: [
        'Up to 15 team members',
        'Unlimited tasks and projects',
        'All modules unlocked',
        '500 AI credits/month',
        'Custom portal branding (logo + accent colour)',
        'Priority email support',
      ],
      cta: 'Start 14-day free trial',
      ctaHref: '/signup?plan=pro',
      ctaStyle: 'filled',
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      priceSub: '',
      annualNote: null,
      badge: null,
      featured: false,
      features: [
        'Unlimited users',
        'Unlimited AI credits',
        'Custom modules',
        'Dedicated onboarding',
        'SSO / SAML (coming soon)',
        'SLA support',
      ],
      cta: 'Contact us',
      ctaHref: 'mailto:hello@notesonthis.com',
      ctaStyle: 'outline',
    },
  ];

  return (
    <div style={{ background: '#F5F0E8', minHeight: '100vh', overflowX: 'hidden' }}>
      <PricingNav />

      {/* ── HERO ─────────────────────────────────────────── */}
      <section style={{ paddingTop: '130px', paddingBottom: '72px', textAlign: 'center', padding: '130px 24px 72px' }}>
        <FadeIn>
          <h1 style={{
            fontFamily: 'Outfit, sans-serif',
            fontSize: 'clamp(36px, 5.5vw, 62px)',
            fontWeight: 300,
            color: '#1a1a1a',
            letterSpacing: '-0.03em',
            lineHeight: 1.1,
            marginBottom: '16px',
          }}>
            Simple, honest pricing
          </h1>
          <p style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '18px',
            fontWeight: 300,
            color: '#5a4a3a',
            lineHeight: 1.6,
            marginBottom: '40px',
          }}>
            Start free. Upgrade when you&apos;re ready.
          </p>

          {/* Toggle */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '14px' }}>
            <span style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: annual ? 300 : 500,
              color: annual ? '#8a7864' : '#1a1a1a',
              transition: 'color 0.2s',
            }}>
              Monthly
            </span>
            <button
              onClick={() => setAnnual(!annual)}
              style={{
                width: '48px',
                height: '26px',
                borderRadius: '100px',
                border: 'none',
                cursor: 'pointer',
                padding: '3px',
                background: annual ? '#8B6914' : 'rgba(139,105,20,0.2)',
                transition: 'background 0.25s ease',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
              }}
              aria-label="Toggle annual billing"
            >
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: '#fff',
                boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                transition: 'transform 0.25s cubic-bezier(0.22,1,0.36,1)',
                transform: annual ? 'translateX(22px)' : 'translateX(0)',
              }} />
            </button>
            <span style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: annual ? 500 : 300,
              color: annual ? '#1a1a1a' : '#8a7864',
              transition: 'color 0.2s',
            }}>
              Annual
            </span>
            {annual && (
              <span style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '12px',
                fontWeight: 500,
                color: '#6b8a5a',
                background: 'rgba(107,138,90,0.1)',
                border: '1px solid rgba(107,138,90,0.25)',
                borderRadius: '100px',
                padding: '3px 10px',
              }}>
                Save 20%
              </span>
            )}
          </div>
        </FadeIn>
      </section>

      {/* ── PRICING CARDS ────────────────────────────────── */}
      <section style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px 100px' }}>
        <FadeIn delay={100}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px',
            alignItems: 'stretch',
          }}>
            {plans.map((plan) => (
              <div
                key={plan.name}
                style={{
                  background: plan.featured ? '#fff' : 'rgba(255,255,255,0.55)',
                  border: plan.featured
                    ? '2px solid #8B6914' :'1px solid rgba(139,105,20,0.15)',
                  borderRadius: plan.featured ? '20px' : '16px',
                  padding: plan.featured ? '36px 32px' : '32px 28px',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  boxShadow: plan.featured
                    ? '0 20px 60px rgba(139,105,20,0.14), 0 4px 16px rgba(139,105,20,0.08)'
                    : '0 4px 20px rgba(26,26,26,0.05)',
                  transform: plan.featured ? 'scale(1.025)' : 'scale(1)',
                  transition: 'box-shadow 0.2s',
                }}
              >
                {/* Badge */}
                {plan.badge && (
                  <div style={{
                    position: 'absolute',
                    top: '-13px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'linear-gradient(135deg, #8B6914 0%, #b07d5a 100%)',
                    color: '#F5F0E8',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    padding: '5px 14px',
                    borderRadius: '100px',
                    whiteSpace: 'nowrap',
                  }}>
                    {plan.badge}
                  </div>
                )}

                {/* Plan name */}
                <div style={{ marginBottom: '20px' }}>
                  <span style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#8B6914',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}>
                    {plan.name}
                  </span>
                </div>

                {/* Price */}
                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-end', gap: '4px' }}>
                  <span style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: plan.price === 'Custom' ? '36px' : '48px',
                    fontWeight: 300,
                    color: '#1a1a1a',
                    letterSpacing: '-0.03em',
                    lineHeight: 1,
                  }}>
                    {plan.price}
                  </span>
                  {plan.priceSub && (
                    <span style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      fontWeight: 300,
                      color: '#8a7864',
                      marginBottom: '6px',
                    }}>
                      {plan.priceSub}
                    </span>
                  )}
                </div>

                {/* Annual note / save badge */}
                <div style={{ minHeight: '24px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {plan.annualNote && (
                    <span style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '12px',
                      fontWeight: 300,
                      color: '#8a7864',
                    }}>
                      {plan.annualNote}
                    </span>
                  )}
                  {(plan as typeof plan & { saveBadge?: string | null }).saveBadge && (
                    <span style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '11px',
                      fontWeight: 500,
                      color: '#6b8a5a',
                      background: 'rgba(107,138,90,0.1)',
                      border: '1px solid rgba(107,138,90,0.25)',
                      borderRadius: '100px',
                      padding: '2px 8px',
                    }}>
                      {(plan as typeof plan & { saveBadge?: string | null }).saveBadge}
                    </span>
                  )}
                </div>

                {/* Divider */}
                <div style={{ height: '1px', background: 'rgba(139,105,20,0.1)', marginBottom: '24px' }} />

                {/* Features */}
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px', display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
                  {plan.features.map((feature) => (
                    <li key={feature} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <Check color={plan.featured ? '#8B6914' : '#8a7864'} />
                      <span style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '14px',
                        fontWeight: 300,
                        color: '#2a1f14',
                        lineHeight: 1.5,
                      }}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Link
                  href={plan.ctaHref}
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    fontWeight: 500,
                    textDecoration: 'none',
                    padding: '13px 24px',
                    borderRadius: '9px',
                    transition: 'opacity 0.2s, background 0.2s',
                    ...(plan.ctaStyle === 'filled'
                      ? {
                          background: 'linear-gradient(135deg, #8B6914 0%, #b07d5a 100%)',
                          color: '#F5F0E8',
                          border: 'none',
                        }
                      : {
                          background: 'transparent',
                          color: '#8B6914',
                          border: '1px solid rgba(139,105,20,0.35)',
                        }),
                  }}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </FadeIn>
      </section>

      {/* ── FAQ ──────────────────────────────────────────── */}
      <section style={{ maxWidth: '720px', margin: '0 auto', padding: '0 24px 120px' }}>
        <FadeIn>
          <h2 style={{
            fontFamily: 'Outfit, sans-serif',
            fontSize: 'clamp(28px, 4vw, 40px)',
            fontWeight: 300,
            color: '#1a1a1a',
            letterSpacing: '-0.025em',
            marginBottom: '8px',
            textAlign: 'center',
          }}>
            Common questions
          </h2>
          <p style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '15px',
            fontWeight: 300,
            color: '#8a7864',
            textAlign: 'center',
            marginBottom: '48px',
          }}>
            Everything you need to know before you start.
          </p>

          <div style={{ borderTop: '1px solid rgba(139,105,20,0.12)' }}>
            {faqs.map((faq, i) => (
              <FadeIn key={faq.q} delay={i * 60}>
                <FAQItem q={faq.q} a={faq.a} />
              </FadeIn>
            ))}
          </div>
        </FadeIn>
      </section>

      {/* ── FOOTER ───────────────────────────────────────── */}
      <footer style={{ background: '#1a1a1a', borderTop: '1px solid rgba(245,240,232,0.08)', padding: '48px 24px 40px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '32px', marginBottom: '40px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '7px',
                  background: 'linear-gradient(135deg, #8B6914 0%, #b07d5a 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                    <path d="M3 4h12M3 8h8M3 12h10" stroke="#F5F0E8" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                </div>
                <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, fontSize: '16px', color: '#F5F0E8' }}>Notes On This</span>
              </div>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 300, color: '#8a7864' }}>
                Think clearly. Build better.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
              {[
                { label: 'Product', href: '#' },
                { label: 'Pricing', href: '/pricing' },
                { label: 'About', href: '#' },
                { label: 'Privacy', href: '#' },
                { label: 'Terms', href: '#' },
              ].map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 300, color: '#8a7864', textDecoration: 'none' }}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          <div style={{ borderTop: '1px solid rgba(245,240,232,0.06)', paddingTop: '24px' }}>
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 300, color: '#5a4a3a' }}>
              © 2026 Notes On This. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
